// Owner event detail page (SLICE-EVENT-TICKET-PASS). Mounted at
// /owner/events/:id. Shows the event + per-tier issued/remaining counts, an
// issue form (single or batch, optional holder name + seat), the resulting
// save links, the shareable public claim link, and a void action.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import type { EventTicketConfig } from '@naya/shared'

import { OwnerApiError } from '../ownerApi.js'
import { useProgram } from '../program/hooks/useProgram.js'
import {
  useEventSummary,
  useIssueTickets,
  type IssuedTicket,
} from '../program/hooks/useEvents.js'

function formatWhen(startsAt: string | undefined): string {
  if (!startsAt) return 'No date set'
  const t = Date.parse(startsAt)
  if (Number.isNaN(t)) return 'No date set'
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Manila',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(t))
}

function peso(centavos: number | null): string {
  if (centavos == null) return 'Free'
  return `₱${(centavos / 100).toLocaleString('en-PH', {
    minimumFractionDigits: centavos % 100 === 0 ? 0 : 2,
  })}`
}

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: program, isLoading } = useProgram(id ?? '')
  const { data: summaryData, refetch } = useEventSummary(id ?? '')
  const issue = useIssueTickets(id ?? '')

  const config = (program?.type_config as EventTicketConfig | null) ?? null
  const summary = summaryData?.summary
  const claimLink =
    summary?.public_code != null
      ? `${window.location.origin}/e/events/${summary.public_code}`
      : null

  const [tierId, setTierId] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [holderName, setHolderName] = useState('')
  const [section, setSection] = useState('')
  const [row, setRow] = useState('')
  const [seatNo, setSeatNo] = useState('')
  const [issued, setIssued] = useState<IssuedTicket[]>([])

  // Default the tier select to the first tier once the summary loads.
  useEffect(() => {
    if (!tierId && summary?.tiers[0]) setTierId(summary.tiers[0].id)
  }, [summary, tierId])

  const selectedTier = useMemo(
    () => summary?.tiers.find((t) => t.id === tierId) ?? null,
    [summary, tierId],
  )
  const soldOut = selectedTier?.remaining === 0 || summary?.remaining === 0

  async function onIssue() {
    if (!id) return
    const seat =
      section.trim() || row.trim() || seatNo.trim()
        ? {
            ...(section.trim() ? { section: section.trim() } : {}),
            ...(row.trim() ? { row: row.trim() } : {}),
            ...(seatNo.trim() ? { seat: seatNo.trim() } : {}),
          }
        : undefined
    try {
      const res = await issue.mutateAsync({
        quantity,
        ...(tierId ? { tier_id: tierId } : {}),
        ...(holderName.trim() ? { holder_name: holderName.trim() } : {}),
        ...(seat ? { seat } : {}),
      })
      setIssued(res.tickets)
      toast.success(
        `Issued ${res.tickets.length} ${res.tier_name} ticket${
          res.tickets.length === 1 ? '' : 's'
        }`,
      )
      setHolderName('')
      setSection('')
      setRow('')
      setSeatNo('')
      void refetch()
    } catch (err) {
      toast.error(
        err instanceof OwnerApiError ? err.userMessage : 'Could not issue tickets.',
      )
    }
  }

  if (isLoading) return <p className="owner-greeting-sub">Loading…</p>
  if (!program || program.pass_type !== 'event_ticket') {
    return (
      <div>
        <h1>Event</h1>
        <p className="owner-greeting-sub">This event could not be found.</p>
        <button
          type="button"
          className="owner-btn"
          onClick={() => navigate('/owner/program')}
        >
          Back to programs
        </button>
      </div>
    )
  }
  if (program.status !== 'active') {
    return (
      <div>
        <h1>{config?.event_name || program.name}</h1>
        <p className="owner-greeting-sub">
          This event is still a draft. Finish setup and activate it before
          issuing tickets.
        </p>
        <button
          type="button"
          className="owner-btn owner-btn-primary"
          onClick={() => navigate(`/owner/program/${program.id}/builder`)}
        >
          Continue setup
        </button>
      </div>
    )
  }

  return (
    <div>
      <h1>{config?.event_name || program.name}</h1>
      <p className="owner-greeting-sub">
        {formatWhen(config?.starts_at)} · {config?.venue_name}
      </p>

      {/* Per-tier counts */}
      <div className="owner-card">
        <div className="owner-card-title">Tickets</div>
        <p className="owner-card-sub">
          {summary
            ? `${summary.issued} issued${
                summary.capacity != null
                  ? ` of ${summary.capacity}`
                  : ''
              }`
            : 'Loading…'}
        </p>
        <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
          {summary?.tiers.map((t) => (
            <div
              key={t.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
                padding: '8px 10px',
                border: '1px solid var(--owner-border, #e2e2e2)',
                borderRadius: 8,
              }}
            >
              <span>
                <strong>{t.name}</strong> · {peso(t.price_centavos)}
              </span>
              <span>
                {t.issued} issued
                {t.capacity != null
                  ? ` · ${t.remaining} left`
                  : ''}
                {t.remaining === 0 ? ' · SOLD OUT' : ''}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Issue form */}
      <div className="owner-card">
        <div className="owner-card-title">Issue tickets</div>
        <div className="program-field-block">
          <label className="program-field-label" htmlFor="issue-tier">
            Tier
          </label>
          <select
            id="issue-tier"
            className="owner-input"
            value={tierId}
            onChange={(e) => setTierId(e.target.value)}
          >
            {summary?.tiers.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({peso(t.price_centavos)})
                {t.remaining === 0 ? ' · sold out' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="program-field-block">
          <label className="program-field-label" htmlFor="issue-qty">
            How many
          </label>
          <input
            id="issue-qty"
            type="number"
            min={1}
            max={200}
            className="owner-input"
            value={quantity}
            onChange={(e) =>
              setQuantity(Math.max(1, Math.min(200, parseInt(e.target.value, 10) || 1)))
            }
          />
        </div>
        {config?.holder_name_required && (
          <div className="program-field-block">
            <label className="program-field-label" htmlFor="issue-holder">
              Attendee name
            </label>
            <input
              id="issue-holder"
              type="text"
              className="owner-input"
              value={holderName}
              placeholder="e.g. Maria Santos"
              onChange={(e) => setHolderName(e.target.value)}
            />
          </div>
        )}
        <div className="program-field-block">
          <span className="program-field-label">
            Seat <span className="program-field-hint">(optional)</span>
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type="text"
              className="owner-input"
              placeholder="Section"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              aria-label="Section"
            />
            <input
              type="text"
              className="owner-input"
              placeholder="Row"
              value={row}
              onChange={(e) => setRow(e.target.value)}
              aria-label="Row"
            />
            <input
              type="text"
              className="owner-input"
              placeholder="Seat"
              value={seatNo}
              onChange={(e) => setSeatNo(e.target.value)}
              aria-label="Seat"
            />
          </div>
          {quantity > 1 && (section || row || seatNo) && (
            <span className="program-field-hint">
              All {quantity} tickets in this batch share the same seat — issue
              separate batches to vary seats.
            </span>
          )}
        </div>
        <button
          type="button"
          className="owner-btn owner-btn-primary"
          disabled={issue.isPending || soldOut}
          onClick={() => void onIssue()}
        >
          {issue.isPending
            ? 'Issuing…'
            : soldOut
              ? 'Sold out'
              : `Issue ${quantity} ticket${quantity === 1 ? '' : 's'}`}
        </button>
      </div>

      {/* Issued tickets (save links) */}
      {issued.length > 0 && (
        <div className="owner-card">
          <div className="owner-card-title">
            {issued.length} ticket{issued.length === 1 ? '' : 's'} issued
          </div>
          <p className="owner-card-sub">
            Share each Add-to-Wallet link with the attendee.
          </p>
          <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
            {issued.map((t) => (
              <div
                key={t.card_id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <span>{t.card_code}</span>
                {t.save_url ? (
                  <button
                    type="button"
                    className="owner-btn"
                    onClick={() => {
                      void navigator.clipboard.writeText(t.save_url!)
                      toast.success('Link copied')
                    }}
                  >
                    Copy wallet link
                  </button>
                ) : (
                  <span className="program-field-hint">
                    Link pending — recover from Customers
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Public claim link */}
      {claimLink && (
        <div className="owner-card">
          <div className="owner-card-title">Public claim link</div>
          <p className="owner-card-sub">
            Share this so people can claim their own ticket and add it to their
            wallet.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <code style={{ wordBreak: 'break-all' }}>{claimLink}</code>
            <button
              type="button"
              className="owner-btn"
              onClick={() => {
                void navigator.clipboard.writeText(claimLink)
                toast.success('Claim link copied')
              }}
            >
              Copy
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className="owner-btn"
        style={{ marginTop: 12 }}
        onClick={() => navigate('/owner/program')}
      >
        Back to programs
      </button>
    </div>
  )
}
